<script lang="ts">
import { ref } from 'vue'

// Shared across the desktop + mobile ProfilePanel instances (both mount at
// once) so an inline save on one is reflected in the other without a reload.
const saved = ref<{
  birthday?: string | Date
  birthdayYearPrivate?: boolean
  location?: string | null
}>({})
</script>

<script setup lang="ts">
import type { CurrentUser, PrivateUserProfile } from '@consumer/types/serializers'
import { faLocationDot, faCakeCandles } from '@fortawesome/pro-light-svg-icons'
import { withAlpha } from '@consumer/helpers/color'
import {
  formatDate,
  DEFAULT_YEAR_FOR_OPTIONAL_YEAR,
} from '@consumer/utils/date'
import UserProfileApi from '@consumer/api/UserProfileApi'
import { showError } from '@consumer/services/flash'
import ContactPhoto from '@consumer/components/ContactPhoto.vue'
import { useFlyOut } from '../FlyOut/composables'
import ProfileLocationDialog from './ProfileLocationDialog.vue'

// Alignment is driven by which menu mounted this panel, not the viewport
// breakpoint — the desktop dropdown is centered, the mobile sheet is
// left-aligned. Each menu passes its own layout so the two never desync.
withDefaults(defineProps<{
  layout?: 'desktop' | 'mobile'
}>(), { layout: 'desktop', })

const centered = computed(() => __props.layout === 'desktop')

// On desktop the panel lives inside a FlyOut that closes on any click; lock it
// open while an inline editor / dialog is active. No-ops in the mobile menu,
// which isn't a FlyOut.
const { setLocked } = useFlyOut()

onBeforeUnmount(() => setLocked(false))

const currentUser = inject<Ref<CurrentUser | null>>('currentUser')

const user = computed(() => currentUser?.value ?? null)

const fullName = computed(() =>
  [user.value?.firstName, user.value?.lastName].filter(Boolean).join(' '),
)

const hasRealPhoto = computed(() => {
  const url = user.value?.profilePictureUrl
  return !!url && !url.includes('gravatar')
})

const panelStyle = computed(() => ({
  backgroundColor: user.value?.profileColor ? withAlpha(user.value.profileColor, 0.6) : undefined,
}))

const location = computed(() => saved.value.location ?? user.value?.location ?? null)

const birthday = computed(() => saved.value.birthday ?? user.value?.birthday ?? null)
const birthdayYearPrivate = computed(
  () => saved.value.birthdayYearPrivate ?? user.value?.birthdayYearPrivate ?? false,
)
const birthdayLabel = computed(() => {
  if (!birthday.value) return null
  return formatDate(birthday.value, {
    format: birthdayYearPrivate.value ? 'MMMM d' : 'MMMM d, yyyy',
  })
})

const editingBirthday = ref(false)
const birthdayDraft = ref<string | null>(null)
const savingBirthday = ref(false)

function startBirthday () {
  birthdayDraft.value = null
  editingBirthday.value = true
  setLocked(true)
}

function cancelBirthday () {
  editingBirthday.value = false
}

async function saveBirthday () {
  if (!birthdayDraft.value || savingBirthday.value) return

  savingBirthday.value = true
  try {
    const keepYearPrivate = birthdayDraft.value.startsWith(`${DEFAULT_YEAR_FOR_OPTIONAL_YEAR}-`)
    const updated = await UserProfileApi.update<PrivateUserProfile>({
      data: { birthdaySettings: { date: birthdayDraft.value, keepYearPrivate } },
    })
    saved.value = {
      ...saved.value,
      birthday: updated.birthday,
      birthdayYearPrivate: updated.birthdayYearPrivate,
    }
    editingBirthday.value = false
  }
  catch (error) {
    showError(error)
  }
  finally {
    savingBirthday.value = false
  }
}

const showLocationDialog = ref(false)

function openLocationDialog () {
  showLocationDialog.value = true
  setLocked(true)
}

function onLocationSaved (value: string) {
  saved.value = { ...saved.value, location: value }
}

// Release the flyout lock once both the inline editor and the dialog are closed.
watch([editingBirthday, showLocationDialog], ([editing, dialogOpen]) => {
  if (!editing && !dialogOpen) setLocked(false)
})
</script>

<template>
  <div
    class="profile-panel flex flex-col gap-4 p-6"
    :class="centered ? 'items-center text-center justify-center h-full' : 'items-start text-left'"
    :style="panelStyle"
  >
    <ContactPhoto
      :name="fullName"
      :profilePictureUrl="hasRealPhoto ? user?.profilePictureUrl : null"
      :backgroundColor="user?.profileColor"
      :size="88"
      :decorated="false"
    />

    <div class="flex flex-col gap-2 w-full text-base" :class="centered ? 'items-center' : 'items-start'">
      <h2 class="text-xl font-bold text-navy leading-tight">
        {{ fullName }}
      </h2>
      <p v-if="user?.username" class="text-v2-tertiary">
        @{{ user.username }}
      </p>

      <span v-if="location" class="flex items-center gap-2 text-v2-tertiary">
        <GIcon :icon="faLocationDot" size="sm"/>{{ location }}
      </span>
      <button
        v-else
        type="button"
        class="link font-semibold flex items-center gap-2"
        @click="openLocationDialog"
      >
        <GIcon :icon="faLocationDot" size="sm"/>Add your location
      </button>

      <template v-if="!editingBirthday">
        <span v-if="birthdayLabel" class="flex items-center gap-2 text-v2-tertiary">
          <GIcon :icon="faCakeCandles" size="sm"/>{{ birthdayLabel }}
        </span>
        <button
          v-else
          type="button"
          class="link font-semibold flex items-center gap-2"
          @click="startBirthday"
        >
          <GIcon :icon="faCakeCandles" size="sm"/>Add your birthday
        </button>
      </template>

      <div v-else class="w-full max-w-[280px] flex flex-col gap-2" @click.stop>
        <GSheetDatepicker
          v-model="birthdayDraft"
          pill
          optionalYear
          noFutureYears
          alwaysSheet
        />
        <div class="flex gap-2">
          <GButton secondary size="small" label="Cancel" class="flex-1" @click="cancelBirthday"/>
          <GButton
            primary
            size="small"
            label="Save"
            class="flex-1"
            :disabled="!birthdayDraft || savingBirthday"
            @click="saveBirthday"
          />
        </div>
      </div>
    </div>

    <ProfileLocationDialog
      v-model:open="showLocationDialog"
      @saved="onLocationSaved"
    />
  </div>
</template>
